import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { DialogService } from 'primeng/dynamicdialog';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';
import {
    MessageInformationEntite,
    MessageInformationModalCloseResult,
} from '../entites/message-information';
import { MessageInformationModalComponent } from '../controles/message-information-modal.component';

const NE_PLUS_AFFICHER_KEY = 'messageInformation.nePlusAfficher';

@Injectable({ providedIn: 'root' })
export class MessageInformationService {
    private readonly http = inject(HttpClient);
    private readonly dialogService = inject(DialogService);

    private readonly baseUrl = `${environment.apiBaseUrl}MessageInformation`;

    private readonly httpOptions = {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
    };

    /**
     * Affiche le message d'information correspondant au GUID si l'utilisateur est éligible.
     */
    afficher(guid: string): void {
        this.obtenirParGuid(guid).subscribe(message => {
            if (!message) {
                return;
            }

            this.dialogService
                .open(MessageInformationModalComponent, {
                    showHeader: false,
                    width: 'min(36rem, 95vw)',
                    styleClass: 'message-information-dialog',
                    data: {
                        message,
                        nePlusAfficherDefault: this.obtenirPreferenceNePlusAfficher(),
                    },
                })
                .onClose.subscribe((result: MessageInformationModalCloseResult | null) => {
                    if (result?.dismiss) {
                        this.dismiss(guid).subscribe();
                    }
                });
        });
    }

    /**
     * Obtient un message d'information par GUID pour l'utilisateur connecté.
     */
    obtenirParGuid(guid: string): Observable<MessageInformationEntite | null> {
        return this.http
            .get<MessageInformationEntite>(`${this.baseUrl}/${guid}`, {
                ...this.httpOptions,
                observe: 'response',
            })
            .pipe(
                map((response: HttpResponse<MessageInformationEntite>) =>
                    response.status === 204 ? null : response.body
                )
            );
    }

    /**
     * Marque le message comme masqué pour l'utilisateur connecté.
     */
    dismiss(guid: string): Observable<void> {
        return this.http.post<void>(`${this.baseUrl}/${guid}/dismiss`, null, this.httpOptions);
    }

    /**
     * Préférence globale « Ne plus afficher » pour tous les messages dismissables.
     */
    obtenirPreferenceNePlusAfficher(): boolean {
        try {
            return localStorage.getItem(NE_PLUS_AFFICHER_KEY) === 'true';
        } catch {
            return false;
        }
    }

    /**
     * Enregistre la préférence globale « Ne plus afficher ».
     */
    enregistrerPreferenceNePlusAfficher(value: boolean): void {
        try {
            localStorage.setItem(NE_PLUS_AFFICHER_KEY, value ? 'true' : 'false');
        } catch {
            // ignore quota / private mode
        }
    }
}
